import loadable from "@loadable/component";
import { inject } from "mobx-react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import type { BlockFragment } from "@ihr-radioedit/inferno-webapi";
import { Container, Heading } from "../../ui";
import { degreesToCardinal, weekdayAbbrev } from "../../services/Weather";
import type { WeatherForecast, CurrentObservations, DailyForecast } from "../../services/Weather";
import "./Weather.style.scss";
import type { Store } from "@inferno/renderer-shared-core";

const WeatherIcon = loadable(() => import("../../components/icons/WeatherIcon.component"), { ssr: false });

interface WeatherProps {
  block: BlockFragment;
  forecast: WeatherForecast;
  store?: Store;
}

interface CurrentProps {
  weather_market?: string | null;
  observations: CurrentObservations;
}

interface SevenDayProps {
  weather_market?: string | null;
  days: DailyForecast[];
}

const CurrentConditions = ({ weather_market, observations }: CurrentProps) => {
  const { t } = useTranslation();
  // iconCode is a non-nullable Int, so always render the icon. (The old truthy
  // guard hid it on WeatherBug code 0 = "Clear Day" -> SunnyIcon.)
  const icon = (
    <div className="weather-conditions-icon">
      <WeatherIcon iconCode={observations.iconCode} />
    </div>
  );

  return (
    <section className="current-weather">
      <div className="current-weather-summary">
        <h2 className="summary-title">{t("current_weather")}</h2>
        <p className="current-location">{weather_market}</p>
        <p className="current-temp">{observations.temperature}&deg;</p>
      </div>
      {icon}
      <div className="current-weather-data">
        <p className="data-title">{t("cond")}</p>
        <ul>
          <li>
            {t("humidity")}: {observations.humidity}%
          </li>
          <li>
            {t("barometer")}: {observations.pressure} in
          </li>
          <li>
            {t("wind")}: {observations.windCurrentSpeed} mph {degreesToCardinal(observations.windCurrentDirection)}
          </li>
        </ul>
      </div>
    </section>
  );
};

const SevenDay = ({ weather_market, days }: SevenDayProps) => {
  const { t } = useTranslation();
  if (!days.length) {
    return null;
  }

  return (
    <section className="seven-day-forecast">
      <Heading level={3}>{t("forecast", { weather_market })}</Heading>
      <div className="forecast-info">
        {days.map((item, i) => (
          <figure key={i}>
            <header>{t(weekdayAbbrev(item.forecastDateLcl))}</header>
            <section className="weather-conditions-icon">
              <WeatherIcon iconCode={item.iconCode} />
            </section>
            <figcaption>
              <section className="day-phrase">{item.iconDescription}</section>
              <p>
                <span className="temperature">
                  {t("Lo")} {item.temperatureLow}°
                </span>
                <span className="seperator">|</span>
                <span className="temperature">
                  {t("Hi")} {item.temperatureHigh}°
                </span>
              </p>
              <p>
                {t("Precip")}: {item.precipProbabilityPercent100 ?? "Unknown"}%
              </p>
            </figcaption>
          </figure>
        ))}
      </div>
    </section>
  );
};

export const Weather = inject("store")(({ block, forecast, store }: WeatherProps) => {
  if (!store) {
    return null;
  }
  const weather_market = store.site.sections.general?.weather_market;
  useEffect(() => {
    store.storeBlock(block);
  });

  // Only current-observations and daily-forecast blocks render here. Any other
  // union member (e.g. forecastType: UV -> SitesWeatherUvForecast) has no view,
  // so bail rather than emit an empty .component-weather container.
  const renderable =
    forecast?.__typename === "SitesWeatherCurrentObservations" ||
    forecast?.__typename === "SitesWeatherDailyForecastList";
  if (!renderable) {
    return null;
  }

  return (
    <Container className="component-weather">
      {forecast.__typename === "SitesWeatherCurrentObservations" ? (
        <CurrentConditions weather_market={weather_market} observations={forecast} />
      ) : null}
      {forecast.__typename === "SitesWeatherDailyForecastList" ? (
        <SevenDay weather_market={weather_market} days={forecast.days} />
      ) : null}
    </Container>
  );
});

export default Weather;
